import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Link, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React, { Dispatch, SetStateAction } from "react";
import { PimSearchProductsFilters } from "../../../../clients/graphqlTypes.js";
import {
  FilterChip,
  FilterChipGroup,
} from "../../../commons/filters/FilterChip.js";
import { FilterPriceRangeChip } from "../../../commons/filters/FilterPriceRangeChip.js";
import { PropertiesFilterChips } from "../../properties/PropertiesFilterChips.js";
import {
  useProductFilterChips_ProductTypesQuery,
  useProductFilterChips_PropertyTypesQuery,
  useProductFilterChips_PropertyTypeValuesQuery,
  useProductFilterChips_SuppliersQuery,
} from "./ProductFilterChips.generated.js";

interface Props<Filters extends PimSearchProductsFilters> {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  onReset?(): void;
  canChangeProductType?: boolean;
  AdditionalChips?: React.ReactNode;
}

export function ProductFilterChips<Filters extends PimSearchProductsFilters>({
  filters,
  setFilters,
  onReset,
  canChangeProductType = true,
  AdditionalChips,
}: Props<Filters>) {
  const { t } = useTranslate(["Product", "ProductSearch", "Global"]);

  const client = useApolloClient();
  const productTypesQuery = useProductFilterChips_ProductTypesQuery({
    client,
    variables: {
      productTypeIds: filters.productTypeIds ?? [],
    },
    fetchPolicy: "cache-and-network",
    skip: !filters.productTypeIds || filters.productTypeIds.length === 0,
  });
  const propertyTypeKeys = filters.properties?.map?.(p => p.key) ?? [];
  const propertyTypesQuery = useProductFilterChips_PropertyTypesQuery({
    client,
    variables: {
      propertyTypeKeys: propertyTypeKeys,
    },
    fetchPolicy: "cache-and-network",
    skip: propertyTypeKeys.length === 0,
  });
  const propertyTypeValueKeys =
    filters.properties?.flatMap?.(p =>
      (p.textFilter ? [p.textFilter.valueText] : []).concat(
        p.textInFilter ? p.textInFilter.valueTextIn : []
      )
    ) ?? [];
  const propertyTypeValuesQuery = useProductFilterChips_PropertyTypeValuesQuery(
    {
      client,
      variables: {
        propertyTypeValueKeys: propertyTypeValueKeys,
      },
      fetchPolicy: "cache-and-network",
      skip: propertyTypeValueKeys.length === 0,
    }
  );
  const suppliersQuery = useProductFilterChips_SuppliersQuery({
    client,
    variables: {
      pimSupplierIds: filters.supplierIds,
    },
    fetchPolicy: "cache-and-network",
    skip: !filters.supplierIds || filters.supplierIds.length === 0,
  });

  const productTypes = getDataOrNull(
    (productTypesQuery.data ?? productTypesQuery.previousData)?.pimProductTypes
  )?.productTypes;
  const getProductTypeName = (id: string) =>
    productTypes?.find?.(productType => productType.id === id)?.label;
  const propertyTypes = getDataOrNull(
    (propertyTypesQuery.data ?? propertyTypesQuery.previousData)
      ?.pimPropertyTypes
  )?.propertyTypes;
  const getPropertyTypeName = (key: string) =>
    propertyTypes?.find?.(propertyType => propertyType.key === key)?.label;
  const propertyTypeValues = getDataOrNull(
    (propertyTypeValuesQuery.data ?? propertyTypeValuesQuery.previousData)
      ?.pimPropertyTypeValues
  )?.propertyTypeValues;
  const getPropertyTypeValueName = (key: string) =>
    propertyTypeValues?.find?.(
      propertyTypeValue => propertyTypeValue.key === key
    )?.label;
  const getSupplierName = (id: string) => {
    const r = (
      suppliersQuery.data ?? suppliersQuery.previousData
    )?.pimSearchSuppliers.suppliers.find(s => s.id === id);
    return r?.title;
  };

  return canShowChips(filters) ? (
    <Stack
      direction={"row"}
      spacing={1}
      flexWrap="wrap"
      alignItems="center"
      useFlexGap
    >
      {filters.productTypeIds && (
        <FilterChipGroup
          // label={t("Product type", { ns: "Product" })}
          values={filters.productTypeIds}
          setValues={
            canChangeProductType
              ? newValues => {
                  setFilters(filters => ({
                    ...filters,
                    productTypeIds:
                      newValues && newValues.length > 0 ? newValues : null,
                  }));
                }
              : undefined
          }
          getValueLabel={getProductTypeName}
        />
      )}
      {filters.supplierIds && (
        <FilterChipGroup
          // label={t("Supplier", { ns: "Product" })}
          values={filters.supplierIds}
          setValues={newValues => {
            setFilters(filters => ({
              ...filters,
              supplierIds: newValues && newValues.length > 0 ? newValues : null,
              ...(!newValues || !newValues.length
                ? { supplierProductCategoryKey: null }
                : undefined),
            }));
          }}
          getValueLabel={getSupplierName}
        />
      )}
      {filters.brands && filters.brands.length > 0 && (
        <FilterChipGroup
          values={filters.brands}
          setValues={newValues => {
            setFilters(filters => ({
              ...filters,
              brands: newValues && newValues.length > 0 ? newValues : null,
            }));
          }}
        />
      )}
      {filters.brandLines && filters.brandLines.length > 0 && (
        <FilterChipGroup
          values={filters.brandLines}
          setValues={newValues => {
            setFilters(filters => ({
              ...filters,
              brandLines: newValues && newValues.length > 0 ? newValues : null,
            }));
          }}
        />
      )}
      <FilterPriceRangeChip
        label={t("List price", { ns: "Product" })}
        valueMin={filters.listPriceMin}
        valueMax={filters.listPriceMax}
        onDelete={() => {
          setFilters(filters => ({
            ...filters,
            listPriceMin: null,
            listPriceMax: null,
          }));
        }}
      />
      {filters.organisationFavourite && (
        <FilterChip
          value={t("Favourites", { ns: "ProductSearch" })}
          onDelete={() =>
            setFilters(filters => ({ ...filters, organisationFavourite: null }))
          }
        />
      )}
      {filters.properties && (
        <PropertiesFilterChips
          values={filters.properties}
          setValues={newValue =>
            setFilters(filters => ({ ...filters, properties: newValue }))
          }
          getKeyLabel={getPropertyTypeName}
          getTextValueLabel={getPropertyTypeValueName}
        />
      )}
      {AdditionalChips}
      {onReset && (
        <Typography variant="body2">
          <Link onClick={onReset} color="secondary" sx={{ cursor: "pointer" }}>
            {t("Clear all", { ns: "Global" })}
          </Link>
        </Typography>
      )}
    </Stack>
  ) : null;
}

export function canShowChips<Filters extends PimSearchProductsFilters>(
  filters: Filters
): boolean {
  return !!(
    filters.listPriceMin ||
    filters.listPriceMax ||
    filters.organisationFavourite ||
    (filters.supplierIds && filters.supplierIds.length > 0) ||
    (filters.productTypeIds && filters.productTypeIds.length > 0) ||
    (filters.brands && filters.brands.length > 0) ||
    (filters.brandLines && filters.brandLines.length > 0) ||
    (filters.properties && filters.properties.length > 0)
  );
}
